import { whenRefreshIdle } from './api/client';
import { hasUnsavedChanges } from './lib/unsaved';

/**
 * Service-worker registration with an explicit update policy.
 *
 * The worker (vite-plugin-pwa, generateSW, skipWaiting + clientsClaim) claims
 * open pages as soon as a new build is installed; that is the
 * `controllerchange` event below. Without a policy the page would keep
 * running the OLD bundle until the user reloads — and a reload mid-install
 * hands out the old bundle again. On a slow phone link that is a long
 * stretch of «loading» with an app already out of step with the API.
 *
 * Policy: when the new worker takes control, reload right away if nothing can
 * be lost (first seconds after start — but never while a token refresh is in
 * flight, its response carries the new refresh token); otherwise reload the
 * moment the app goes to the background, unless an editor holds unsaved
 * changes, in which case wait for the next background with a clean slate. A
 * long-lived open PWA also re-checks for updates when it comes back to the
 * foreground (at most every 5 min).
 */
const BOOT_PHASE_MS = 20_000;
const UPDATE_CHECK_MIN_INTERVAL_MS = 5 * 60_000;

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  const sw = navigator.serviceWorker;
  const startedAt = Date.now();
  const hadController = sw.controller !== null;
  let registration: ServiceWorkerRegistration | null = null;
  let reloading = false;
  let reloadWhenHidden = false;
  let lastUpdateCheck = startedAt;

  const reload = (): void => {
    if (reloading) return;
    reloading = true;
    void whenRefreshIdle().then(() => {
      window.location.reload();
      // If the navigation did not happen (a beforeunload prompt was
      // cancelled), re-arm so a later opportunity can still reload.
      window.setTimeout(() => {
        reloading = false;
      }, 3_000);
    });
  };

  sw.addEventListener('controllerchange', () => {
    // First ever install: the page already runs the freshly served bundle.
    if (!hadController) return;
    if (Date.now() - startedAt < BOOT_PHASE_MS && !hasUnsavedChanges()) reload();
    else reloadWhenHidden = true;
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (reloadWhenHidden && !hasUnsavedChanges()) reload();
      return;
    }
    if (Date.now() - lastUpdateCheck < UPDATE_CHECK_MIN_INTERVAL_MS) return;
    lastUpdateCheck = Date.now();
    registration?.update().catch(() => {});
  });

  window.addEventListener('load', () => {
    sw.register('/sw.js', { scope: '/' })
      .then((r) => {
        registration = r;
      })
      .catch(() => {});
  });
}
